import React, { useRef } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import i18n from "@i18n";

import { trackEvent } from "@utils/api/tracker";
import { compassColors } from "@utils/styling";

import { useTrackInViewport } from "src/hooks/viewport";
import { appConfig } from "src/config";
import {
  isCoreDirect,
  isEnLanguage,
  isEsLanguage,
  isUS,
} from "src/utils/userSegment";
import { macroCalculatorURL } from "src/free-products/macro-calculator/Layout";
import { calorieCalculatorURL } from "src/free-products/calorie-deficit-calculator/Layout";
import { personalityQuizURL } from "src/free-products/personality-quiz/Layout";
import { breakpointMobileExtendedPixels } from "src/design-system/styles/breakpoints";
import { capitalizeFirstLetter } from "src/primitive/string";

import facebook2 from "./images/footer_facebook_icon_2.png";
import instagram2 from "./images/footer_instagram_icon_2.png";
import linkedin2 from "./images/footer_linkedin_icon_2.png";
import twitter2 from "./images/footer_twitter_icon_2.png";

import { NoomLogo } from "../Logo";
import { useFooterOverride } from "./SingleUseFooter";
import { LegalLinks } from "./LegalLinks";

const logos = {
  facebook: facebook2,
  instagram: instagram2,
  linkedin: linkedin2,
  twitter: twitter2,
};
const orderedLogos: (keyof typeof logos)[] = [
  "facebook",
  "twitter",
  "instagram",
  "linkedin",
];

type FooterColumn = {
  title: string;
  href?: string;
  trackEvent?: string;
  items: {
    label: string;
    href: string;
    trackEvent: string;
    hasRef?: boolean;
    trackEventProperties?: { [key: string]: string };
  }[];
};

const footerCols: FooterColumn[] = [
  {
    title: i18n.t("footer:companyHeader"),
    items: [
      {
        label: i18n.t("footer:aboutUs"),
        href: `${appConfig.BLOG_DOMAIN}/${i18n.t("footer:aboutUsLink")}`,
        trackEvent: "ClickedAboutUsFooterLink",
      },
      {
        label: i18n.t("footer:careers"),
        href: `${appConfig.BLOG_DOMAIN}/${i18n.t("footer:careersLink")}`,
        trackEvent: "ClickedCareersFooterLink",
      },
      {
        label: i18n.t("footer:research"),
        href: `${appConfig.BLOG_DOMAIN}/${i18n.t("footer:researchLink")}`,
        trackEvent: "ClickedResearchFooterLink",
      },
      {
        label: i18n.t("footer:press"),
        href: `${appConfig.BLOG_DOMAIN}/${i18n.t("footer:pressLink")}`,
        trackEvent: "ClickedPressFooterLink",
      },
      {
        label: i18n.t("footer:blog"),
        href: `${appConfig.BLOG_DOMAIN}/${i18n.t("footer:blogLink")}`,
        trackEvent: "ClickedBlogFooterLink",
      },
    ],
  },
  isEsLanguage() && {
    title: i18n.t("footer:resourcesHeader"),
    href: `${appConfig.BLOG_DOMAIN}/${i18n.t("footer:supportLink")}`,
    trackEvent: "ClickedSupportFooterLink",
    items: [],
  },
  !isEsLanguage() && {
    title: i18n.t("footer:resourcesHeader"),
    items: [
      {
        label: i18n.t("footer:support"),
        href: `${appConfig.BLOG_DOMAIN}/${i18n.t("footer:supportLink")}`,
        trackEvent: "ClickedSupportFooterLink",
      },
    ],
  },
  {
    title: i18n.t("footer:languagesHeader"),
    items: [
      {
        label: i18n.t("language:english"),
        href: "https://www.noom.com/en/",
        trackEvent: "ClickedFooterLanguageLink",
        trackEventProperties: {
          languageClicked: "en",
        },
      },
      {
        label: i18n.t("language:german"),
        href: "https://www.noom.com/de/",
        trackEvent: "ClickedFooterLanguageLink",
        trackEventProperties: {
          languageClicked: "de",
        },
      },
      {
        label: i18n.t("language:spanish"),
        href: "https://www.noom.com/es/",
        trackEvent: "ClickedFooterLanguageLink",
        trackEventProperties: {
          languageClicked: "es",
        },
      },
      {
        label: i18n.t("language:korean"),
        href: "https://www.noom.com/kr/",
        trackEvent: "ClickedFooterLanguageLink",
        trackEventProperties: {
          languageClicked: "ko",
        },
      },
    ],
  },
].filter((x) => x);

if (isUS()) {
  const loseWeightItem = {
    label: i18n.t("footer:loseWeight"),
    href: `${appConfig.BLOG_DOMAIN}/${i18n.t("navBar:weightLossLink")}`,
    trackEvent: "ClickedLoseWeightFooterLink",
    // Used for tracking the user scrolling to the first footer link.
    hasRef: true,
  };
  const relieveStressItem = {
    label: i18n.t("footer:relieveStress"),
    href: `${appConfig.BLOG_DOMAIN}/mood/`,
    trackEvent: "ClickedRelieveStressFooterLink",
  };
  const noomVibeItem = {
    label: i18n.t("footer:noomVibe"),
    href: `https://noomvibe.app/landing`,
    trackEvent: "ClickedNoomVibeFooterLink",
  };
  const freeToolsItems = [
    {
      label: i18n.t("footer:personalityQuiz"),
      href: personalityQuizURL(),
      trackEvent: "ClickedPersonalityQuizFooterLink",
    },
    {
      label: i18n.t("footer:calorieCalculator"),
      href: calorieCalculatorURL(),
      trackEvent: "ClickedCalorieCalculatorFooterLink",
    },
    {
      label: i18n.t("footer:macroCalculator"),
      href: macroCalculatorURL(),
      trackEvent: "ClickedMacroCalculatorFooterLink",
    },
  ];

  if (isCoreDirect() && isEnLanguage()) {
    footerCols.unshift(
      {
        title: i18n.t("footer:forIndividualsHeader"),
        items: [loseWeightItem, relieveStressItem].concat(
          freeToolsItems,
          noomVibeItem
        ),
      },
      {
        title: i18n.t("footer:forOrganizationsHeader"),
        items: [
          {
            label: i18n.t("footer:employersNew"),
            href: "https://www.noom.com/for-work/?nfw_section=employers",
            trackEvent: "ClickedForEmployersFooterLink",
          },
          {
            label: i18n.t("footer:healthPlans"),
            href: "https://www.noom.com/for-work/?nfw_section=healthPlans",
            trackEvent: "ClickedHealthPlansFooterLink",
          },
          {
            label: i18n.t("footer:consultantsAndBrokers"),
            href: "https://www.noom.com/for-work/?nfw_section=consultantsAndBrokers",
            trackEvent: "ClickedConsultantsAndBrokersFooterLink",
          },
          {
            label: i18n.t("footer:healthSystems"),
            href: "https://www.noom.com/health-systems",
            trackEvent: "ClickedHealthSystemsFooterLink",
          },
          {
            label: i18n.t("footer:resources"),
            href: "https://www.noom.com/for-work/resources",
            trackEvent: "ClickedResourcesAndBrokersFooterLink",
          },
        ],
      }
    );
  } else {
    // Adds products sold only in the US to US version of the footer
    footerCols.unshift(
      {
        title: i18n.t("footer:productHeader"),
        items: [
          loseWeightItem,
          relieveStressItem,
          {
            label: i18n.t("footer:forSmallBusiness"),
            href: `${appConfig.BLOG_DOMAIN}/noom-for-teams/`,
            trackEvent: "ClickedForSmallBusinessFooterLink",
          },
          {
            label: i18n.t("footer:employers"),
            href: `${appConfig.BLOG_DOMAIN}/business/`,
            trackEvent: "ClickedForEmployersFooterLink",
          },
        ],
      },
      {
        title: i18n.t("footer:freeToolsHeader"),
        items: freeToolsItems,
      }
    );
  }
}

// Note: Keeping the hard-coded breakpoints in this files, as they didn't match the ones in the design system
const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  background-color: ${compassColors.white};
  font-weight: 300;

  color: ${compassColors.white};
  width: 100%;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${compassColors.blueberry};
  padding: 75px 20px 20px;
`;

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  @media all and (max-width: ${breakpointMobileExtendedPixels}) {
    flex-wrap: wrap;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  width: 200px;
  font-weight: 400;

  @media all and (max-width: ${breakpointMobileExtendedPixels}) {
    width: 50%;
    margin-bottom: 20px;
  }

  @media all and (min-width: 900px) {
    width: auto;
  }
`;

const ColumnMobileFull = styled(Column)`
  @media all and (max-width: ${breakpointMobileExtendedPixels}) {
    width: 100%;
  }
`;

const ColumnHeading = styled.span`
  color: ${compassColors.white};
  text-transform: uppercase;
`;

const ColumnHeadingLink = styled.a`
  text-decoration: none;
  color: ${compassColors.white};
  text-transform: uppercase;
`;

const ColumnList = styled.ul`
  padding-left: 0;
`;

const ColumnListItem = styled.li`
  text-decoration: none;
  list-style: none;
  font-size: 13pt;

  @media all and (min-width: 900px) {
    width: 200px;
  }
`;

const ColumnListItemLink = styled.a`
  text-decoration: none;
  display: flex;
  align-items: center;
  color: ${compassColors.white};
  padding: 5px 0;
  font-size: 14px;
  min-height: 25px;
`;

const SocialLink = styled.div`
  display: inline-block;
  margin-right: 20px;
`;

const fontFooterText = css`
  font-size: 14px;
  font-weight: 300;
`;

const CopyrightText = styled.p`
  flex-grow: 1;
  padding: 0;
  color: ${compassColors.white};
  white-space: nowrap;
  margin: 35px 0 0 6px;
  ${fontFooterText}

  @media all and (max-width: ${breakpointMobileExtendedPixels}) {
    align-self: center;
  }
`;

const CopyrightTerms = styled.div`
  margin: 20px 0 0 10px;
  padding: 0;
  text-decoration: underline;
  color: ${compassColors.white};
  ${fontFooterText}
`;

/**
 * Footer shown on the Landing page.
 */
const FooterLanding = () => {
  const firstLinkRef = useRef(null);
  useTrackInViewport(firstLinkRef, "ScrolledToFooterNav");

  useFooterOverride();

  const currentYear = new Date().getFullYear();
  return (
    <Footer>
      <MainContent>
        <Columns>
          <ColumnMobileFull>
            <NoomLogo showPill={false} css={{ height: 35, padding: 10 }} />
          </ColumnMobileFull>
          {footerCols.map((col) => (
            <Column key={col.title}>
              {col.href ? (
                <ColumnHeadingLink
                  href={col.href}
                  onClick={() => {
                    if ("trackEvent" in col) {
                      trackEvent(col.trackEvent);
                    }
                  }}
                >
                  {col.title}
                </ColumnHeadingLink>
              ) : (
                <ColumnHeading>{col.title}</ColumnHeading>
              )}
              <ColumnList>
                {col.items.map((item) => (
                  <ColumnListItem
                    key={item.label}
                    ref={item.hasRef && firstLinkRef}
                  >
                    <ColumnListItemLink
                      href={item.href}
                      onClick={() => {
                        if ("trackEvent" in item) {
                          trackEvent(
                            item.trackEvent,
                            item.trackEventProperties || {}
                          );
                        }
                      }}
                    >
                      {item.label}
                    </ColumnListItemLink>
                  </ColumnListItem>
                ))}
              </ColumnList>
            </Column>
          ))}
        </Columns>
        <div>
          {orderedLogos.map((site) => (
            <SocialLink key={site}>
              <a
                rel="nofollow noopener noreferrer"
                target="_blank"
                href={i18n.t(`footer:${site}Link`)}
                onClick={() =>
                  trackEvent(`Clicked${capitalizeFirstLetter(site)}FooterLink`)
                }
              >
                <img src={logos[site]} alt={`${site}-logo`} />
              </a>
            </SocialLink>
          ))}
        </div>

        <Columns>
          <ColumnMobileFull>
            <CopyrightText>
              {i18n.t("footer:copyright", { currentYear })}
            </CopyrightText>
          </ColumnMobileFull>
          <ColumnMobileFull>
            <CopyrightTerms>
              <LegalLinks removeSupport />
            </CopyrightTerms>
          </ColumnMobileFull>
        </Columns>
      </MainContent>
    </Footer>
  );
};

export default FooterLanding;
